import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../Redux/store';
import { translations } from '../utils/translations';

interface Product {
    id: number;
    name: string;
}

interface Customer {
    id: number;
    name: string;
}

export default function AddSaleComponent() {
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];
    const [products, setProducts] = useState<Product[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [product_id, setProductId] = useState<number | null>(null);
    const [customer_id, setCustomerId] = useState<number | null>(null);
    const [walkInCustomerName, setWalkInCustomerName] = useState<string>('Walking customer');
    const [isWalkInCustomer, setIsWalkInCustomer] = useState<boolean>(true);
    const [quantity, setQuantity] = useState<number>(1);
    const [selling_price, setSalePrice] = useState<string>('');
    const [amount_paid, setAmountPaid] = useState<string>('');
    const [dateSold, setDateSold] = useState<string>(new Date().toISOString().slice(0, 10));
    const [paymentType, setPaymentType] = useState<string>('cash');
    const [redirect, setRedirect] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showDropdown, setShowDropdown] = useState<boolean>(false);

    const fetchProducts = async (term: string) => {
        try {
            const response = await axios.get(`https://api.sominnovations.xyz/Branches/products/?search=${term}`);
            setProducts(response.data.results);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await axios.get('https://api.sominnovations.xyz/Branches/customers/');
            setCustomers(response.data.results);
        } catch (error) {
            console.error('Error fetching customers:', error);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    useEffect(() => {
        if (searchTerm) {
            fetchProducts(searchTerm);
            setShowDropdown(true);
        } else {
            setShowDropdown(false);
        }
    }, [searchTerm]);

    const handleProductSelect = (product: Product) => {
        setProductId(product.id);
        setSearchTerm(product.name); // Set the search term to the selected product name
        setShowDropdown(false); // Hide the dropdown after selection
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!product_id) {
            alert("Please select a product from the list.");
            return;
        }

        if (!isWalkInCustomer && !customer_id) {
            alert("Please select a customer or check 'Walk-in Customer'.");
            return;
        }

        if (parseFloat(selling_price) < 0 || parseFloat(amount_paid) < 0) {
            alert("Price and Amount Paid must be positive.");
            return;
        }

        const saleData = {
            product_id,
            quantity,
            selling_price,
            amount_paid,
            payment_type: paymentType,
            date_sold: dateSold,
            ...(isWalkInCustomer ? { walk_in_customer: walkInCustomerName } : { customer_id }),
        };
        try {
            await axios.post('https://api.sominnovations.xyz/Branches/sales/', saleData);
            alert('Sale added successfully!');
            setRedirect(true);
        } catch (error: any) {
            if (error.response) {
                console.log("Error response data:", error.response.data);
            } else {
                console.error("Error", error.message);
            }
        }
    };

    if (redirect) {
        return <Navigate to="/sales" />;
    }

    return (
        <div className="container mt-1" style={{ maxWidth: "800px", padding: '20px', border: '1px solid #ccc', borderRadius: '8px' }}>
            <h2 className="text-center mb-4">{t.addSale}</h2>
            <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                    <Col md={6} style={{ position: 'relative' }}>
                        <Form.Group controlId="formProductSearch">
                            <Form.Label>Search Product</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Search product by name"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Form.Group>
                        {showDropdown && products.length > 0 && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                zIndex: 10,
                                maxHeight: '150px',
                                overflowY: 'auto'
                            }}>
                                {products.map(product => (
                                    <div
                                        key={product.id}
                                        onClick={() => handleProductSelect(product)}
                                        style={{
                                            padding: '8px',
                                            cursor: 'pointer'
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                    >
                                        {product.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Col>

                    <Col md={6}>
                        <Form.Group controlId="formQuantity">
                            <Form.Label>{t.qty}</Form.Label>
                            <Form.Control type="number" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} min="1" required />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group controlId="formIsWalkInCustomer" className="mb-3">
                    <Form.Check
                        type="checkbox"
                        label="Walk-in Customer"
                        checked={isWalkInCustomer}
                        onChange={(e) => setIsWalkInCustomer(e.target.checked)}
                    />
                </Form.Group>

                {isWalkInCustomer ? (
                    <Form.Group controlId="formWalkInCustomerName" className="mb-3">
                        <Form.Label>Walk-in Customer Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={walkInCustomerName}
                            onChange={(e) => setWalkInCustomerName(e.target.value)}
                            required
                        />
                    </Form.Group>
                ) : (
                    <Form.Group controlId="formCustomer" className="mb-3">
                        <Form.Label>{t.customers}</Form.Label>
                        <Form.Control as="select" onChange={(e) => setCustomerId(Number(e.target.value))} required>
                            <option value="">Select Customer</option>
                            {customers.map(customer => (
                                <option key={customer.id} value={customer.id}>
                                    {customer.name}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                )}

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="formSalePrice">
                            <Form.Label>{t.sellingPrice}</Form.Label>
                            <Form.Control type="text" value={selling_price} onChange={(e) => setSalePrice(e.target.value)} required />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group controlId="formAmountPaid">
                            <Form.Label>{t.amountPaid}</Form.Label>
                            <Form.Control type="text" value={amount_paid} onChange={(e) => setAmountPaid(e.target.value)} required />
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="formDateSold">
                            <Form.Label>Date Sold</Form.Label>
                            <Form.Control type="date" value={dateSold} onChange={(e) => setDateSold(e.target.value)} required />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group controlId="formPaymentType">
                            <Form.Label>{t.paymentType}</Form.Label>
                            <Form.Control as="select" value={paymentType} onChange={(e) => setPaymentType(e.target.value)} required>
                                <option value="cash">Cash</option>
                                <option value="check">Check</option>
                                <option value="mpesa">Mpesa</option>
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>

                <Button variant="primary" type="submit" className="w-100">
                    {t.submitSale}
                </Button>
            </Form>
        </div>
    );
}
